/**
 * Datasources Page
 * Manage datasources - upload files, name collections, view status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FolderOpen, Trash2, FileText, Database, Plus, X, Check, ChevronDown, Search, Zap, Globe, Code, DollarSign } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { Button, Spinner } from '../../components/ui';
import { Input } from '../../components/ui/Input';
import { ConfirmModal } from '../../components/ui';
import { KnowledgeSidebar } from '../KnowledgeBasePage/components/KnowledgeSidebar';
import api from '../../services/api';
import type { EmbeddingConfig, EmbeddingModelInfo } from '../../services/api';
import styles from './DatasourcesPage.module.css';

interface DatasourceFile {
  name: string;
  size: number;
  type: string;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
}

interface Datasource {
  id: string;
  name: string;
  description?: string;
  type: string;
  status: 'pending' | 'extracting' | 'embedding' | 'complete' | 'error';
  vectorCount?: number;
  files?: DatasourceFile[];
  createdAt: string;
  updatedAt: string;
}

export interface DatasourcesPageProps {
  embedded?: boolean;
}

export const DatasourcesPage: React.FC<DatasourcesPageProps> = ({ embedded = false }) => {
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Create datasource form state
  const [newDatasourceName, setNewDatasourceName] = useState('');
  const [newDatasourceDescription, setNewDatasourceDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  
  // Delete confirmation modal state
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null);
  const [deleteModalName, setDeleteModalName] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // RAG model selection state
  const [embeddingConfig, setEmbeddingConfig] = useState<EmbeddingConfig | null>(null);
  const [selectedUseCase, setSelectedUseCase] = useState<string>('budget');
  const [showModelDetails, setShowModelDetails] = useState(false);

  const loadDatasources = useCallback(async () => {
    try {
      setLoading(true);
      const [dsResponse, embeddingResponse] = await Promise.all([
        api.listAllDatasources(),
        api.getEmbeddingConfig(),
      ]);
      if (dsResponse.success && dsResponse.data) {
        const data = Array.isArray(dsResponse.data) ? dsResponse.data : [];
        setDatasources(data);
      }
      if (embeddingResponse.success && embeddingResponse.data) {
        setEmbeddingConfig(embeddingResponse.data);
        setSelectedUseCase(embeddingResponse.data.current.useCase || 'budget');
      }
    } catch (error) {
      console.error('Failed to load datasources:', error);
      setDatasources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDatasources();
  }, [loadDatasources]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateDatasource = async () => {
    if (!newDatasourceName.trim() || selectedFiles.length === 0) return;

    setIsCreating(true);
    try {
      // Apply selected RAG model before creating datasource
      if (selectedUseCase && embeddingConfig?.current.useCase !== selectedUseCase) {
        await api.setEmbeddingUseCase(selectedUseCase);
      }

      // Create the datasource first
      const createResponse = await api.createGlobalDatasource({
        name: newDatasourceName,
        description: newDatasourceDescription,
        type: 'file',
      });

      if (!createResponse.success) {
        throw new Error('Failed to create datasource');
      }

      const datasourceId = createResponse.data.id;

      // Upload files to the datasource
      for (const file of selectedFiles) {
        try {
          await api.uploadToDatasource(datasourceId, file, (progress) => {
            setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          });
        } catch (uploadError) {
          console.error(`Failed to upload ${file.name}:`, uploadError);
        }
      }

      // Reset form and reload
      setNewDatasourceName('');
      setNewDatasourceDescription('');
      setSelectedFiles([]);
      setUploadProgress({});
      setShowCreateModal(false);
      await loadDatasources();
    } catch (error) {
      console.error('Failed to create datasource:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDatasource = (id: string, name: string) => {
    setDeleteModalId(id);
    setDeleteModalName(name);
  };
  
  const confirmDeleteDatasource = async () => {
    if (!deleteModalId) return;
    
    try {
      await api.deleteGlobalDatasource(deleteModalId);
      await loadDatasources();
    } catch (error) {
      console.error('Failed to delete datasource:', error);
    } finally {
      setDeleteModalId(null);
      setDeleteModalName('');
    }
  };
  
  const cancelDeleteDatasource = () => {
    setDeleteModalId(null);
    setDeleteModalName('');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'complete': return styles.statusComplete;
      case 'extracting':
      case 'embedding': return styles.statusProcessing;
      case 'error': return styles.statusError;
      default: return styles.statusPending;
    }
  };

  // Sidebar content for AppLayout (matching Chats/Assistants pattern)
  const datasourcesSidebar = (
    <KnowledgeSidebar
      headerAction={
        <Button
          type="button"
          variant="primary"
          size="sm"
          leftIcon={<Plus size={16} />}
          onClick={() => setShowCreateModal(true)}
        >
          Create
        </Button>
      }
    >
      <div className={styles.sidebarFilters}>
        <h3 className={styles.sidebarSectionTitle}>FILTERS</h3>

        <div className={styles.sidebarFilterGroup}>
          <Input
            isSearch
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>TYPE</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <div className={styles.checkboxGroup}>
              {['Code', 'Confluence', 'Jira', 'X-ray', 'File', 'Google', 'Azure Devops Wiki', 'Provider', 'Bedrock'].map(type => (
                <label key={type} className={styles.checkboxItem}>
                  <input type="checkbox" />
                  <span>{type}</span>
                  {type === 'X-ray' && <span className={styles.newBadge}>NEW</span>}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>PROJECT</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <select className={styles.sidebarFilterSelect}>
              <option value="">Project</option>
            </select>
          </div>
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>CREATED BY</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <label className={styles.checkboxItem}>
              <span>Created by</span>
              <input type="checkbox" />
              <span>Me</span>
            </label>
            <select className={styles.sidebarFilterSelect}>
              <option value="">Created By</option>
            </select>
          </div>
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>STATUS</span>
          </button>
        </div>
      </div>
    </KnowledgeSidebar>
  );

  if (loading) {
    return (
      <AppLayout sidebar={datasourcesSidebar} hideLayout={embedded}>
        <div className={styles.loading}>
          <Spinner size="md" label="Loading datasources..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout sidebar={datasourcesSidebar} hideLayout={embedded}>
      <div className={styles.page}>

        {datasources.length === 0 ? (
          <div className={styles.emptyState}>
            <FolderOpen size={64} />
            <h2>No datasources yet</h2>
            <p>Create your first datasource to upload documents for your AI assistants.</p>
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={() => setShowCreateModal(true)}
            >
              Create Datasource
            </Button>
          </div>
        ) : (
          <div className={styles.datasourceGrid}>
            {datasources.map(ds => (
              <div key={ds.id} className={styles.datasourceCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <Database size={24} />
                  </div>
                  <div className={styles.cardInfo}>
                    <h3>{ds.name}</h3>
                    {ds.description && <p>{ds.description}</p>}
                  </div>
                  <span className={`${styles.statusBadge} ${getStatusBadgeClass(ds.status)}`}>
                    {ds.status}
                  </span>
                </div>

                <div className={styles.cardStats}>
                  <div className={styles.stat}>
                    <FileText size={16} />
                    <span>{ds.files?.length || 0} files</span>
                  </div>
                  <div className={styles.stat}>
                    <Database size={16} />
                    <span>{ds.vectorCount || 0} vectors</span>
                  </div>
                </div>

                {ds.files && ds.files.length > 0 && (
                  <div className={styles.fileList}>
                    {ds.files.slice(0, 3).map((file, idx) => (
                      <div key={idx} className={styles.fileItem}>
                        <FileText size={14} />
                        <span>{file.name}</span>
                        <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                    {ds.files.length > 3 && (
                      <div className={styles.moreFiles}>
                        +{ds.files.length - 3} more files
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.cardActions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDatasource(ds.id, ds.name)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Datasource Modal */}
        {showCreateModal && (
          <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Create Datasource</h2>
                <button 
                  className={styles.modalClose} 
                  onClick={() => setShowCreateModal(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Datasource Name *</label>
                  <Input
                    value={newDatasourceName}
                    onChange={(e) => setNewDatasourceName(e.target.value)}
                    placeholder="e.g., Product Documentation"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Description</label>
                  <Input
                    value={newDatasourceDescription}
                    onChange={(e) => setNewDatasourceDescription(e.target.value)}
                    placeholder="Brief description of contents"
                  />
                </div>

                {/* RAG Model Selection */}
                {embeddingConfig && (
                  <div className={styles.formGroup}>
                    <label>RAG Embedding Model</label>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>
                      Choose how your documents will be embedded for retrieval
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {[
                        { key: 'budget', label: 'Budget', desc: 'OpenAI Small — Fast & affordable', icon: <DollarSign size={16} /> },
                        { key: 'enterprise', label: 'Enterprise', desc: 'OpenAI Large — High accuracy', icon: <Zap size={16} /> },
                        { key: 'code', label: 'Code', desc: 'Voyage AI — Best for code', icon: <Code size={16} /> },
                        { key: 'multilingual', label: 'Multilingual', desc: 'Cohere — 100+ languages', icon: <Globe size={16} /> },
                      ].map(preset => (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => setSelectedUseCase(preset.key)}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '10px 12px',
                            border: selectedUseCase === preset.key
                              ? '2px solid var(--accent-color, #6366f1)'
                              : '1px solid var(--border-color, #333)',
                            borderRadius: '8px',
                            background: selectedUseCase === preset.key
                              ? 'var(--accent-bg, rgba(99,102,241,0.1))'
                              : 'var(--bg-secondary, #1a1a2e)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            color: 'inherit',
                          }}
                        >
                          <span style={{ marginTop: '2px', color: selectedUseCase === preset.key ? 'var(--accent-color, #6366f1)' : 'var(--text-secondary)' }}>
                            {preset.icon}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{preset.label}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{preset.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    {/* Show selected model details */}
                    {embeddingConfig.presets[selectedUseCase] && (
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '8px 12px', 
                        background: 'var(--bg-tertiary, #111)', 
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        gap: '16px',
                      }}>
                        <span>Provider: <strong style={{ color: 'var(--text-primary)' }}>{embeddingConfig.presets[selectedUseCase].provider}</strong></span>
                        <span>Model: <strong style={{ color: 'var(--text-primary)' }}>{embeddingConfig.presets[selectedUseCase].model}</strong></span>
                        <span>Dimensions: <strong style={{ color: 'var(--text-primary)' }}>{embeddingConfig.presets[selectedUseCase].dimensions}</strong></span>
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>Upload Files *</label>
                  <div className={styles.uploadArea}>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className={styles.fileInput}
                      id="file-upload"
                      accept=".pdf,.txt,.md,.doc,.docx,.json,.csv"
                    />
                    <label htmlFor="file-upload" className={styles.uploadLabel}>
                      <Upload size={32} />
                      <span>Drop files here or click to upload</span>
                      <small>PDF, TXT, MD, DOC, DOCX, JSON, CSV</small>
                    </label>
                  </div>
                </div>

                {selectedFiles.length > 0 && (
                  <div className={styles.selectedFiles}>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className={styles.selectedFile}>
                        <FileText size={16} />
                        <span>{file.name}</span>
                        <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                        {uploadProgress[file.name] !== undefined ? (
                          <span className={styles.progress}>
                            {uploadProgress[file.name] === 100 ? (
                              <Check size={16} className={styles.checkIcon} />
                            ) : (
                              `${uploadProgress[file.name]}%`
                            )}
                          </span>
                        ) : (
                          <button 
                            className={styles.removeFile}
                            onClick={() => handleRemoveFile(index)}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateDatasource}
                  disabled={!newDatasourceName.trim() || selectedFiles.length === 0 || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create Datasource'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={!!deleteModalId}
          title="Delete Datasource"
          message={`Are you sure you want to delete "${deleteModalName}"? This will permanently remove all associated files and vectors.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={confirmDeleteDatasource}
          onClose={cancelDeleteDatasource}
        />
      </div>
    </AppLayout>
  );
};

export default DatasourcesPage;
